import {Record} from 'immutable';

import {makeReducer, match, type PayloadReducer} from 'app/util/redux/reducer-helpers';
import * as FeedItemActions from 'web-app/react/entities/feed/items/actions';
import {ObservationVariant as ObservationVariantValues} from 'web-app/react/entities/observations/constants';

import * as Actions from './actions';
import * as TypedActions from './typed-actions';
import {type InitialValues as ShowWithInitialValuesPayload} from './typed-actions';

type ObservationVariant = typeof ObservationVariantValues[keyof typeof ObservationVariantValues];

export type DraftManagerModalStateShape = {
    isVisible: boolean;
    newPostRecipients?: string;
    feedItem?: any;
    context: string;
    singleDraftMode: boolean;
    disablePosts: boolean;
    disableObservations: boolean;
    observationVariant: ObservationVariant;
    selectedRecipients?: string;
    initialValues?: ShowWithInitialValuesPayload;
};

export const DraftManagerModalStateRecord = Record<DraftManagerModalStateShape>({
    isVisible: false,
    newPostRecipients: undefined,
    feedItem: undefined,
    context: '',
    singleDraftMode: false,
    disablePosts: false,
    disableObservations: false,
    observationVariant: ObservationVariantValues.REGULAR_OBSERVATION,
    selectedRecipients: undefined,
    initialValues: undefined,
});

export type DraftManagerModalState = ReturnType<typeof DraftManagerModalStateRecord>;

const showReducer: PayloadReducer<DraftManagerModalState, Actions.ShowPayload> = (state, {payload}) => {
    return state.merge({
        isVisible: true,
        newPostRecipients: payload.newPostRecipients,
        context: payload.context,
        feedItem: payload.feedItem,
        disablePosts: payload.disablePosts,
        disableObservations: payload.disableObservations,
        observationVariant: payload.observationVariant,
        selectedRecipients: undefined,
    });
};

const showObservationReducer: PayloadReducer<DraftManagerModalState, Actions.ShowObservationPayload> = (
    state,
    {payload},
) => {
    return state.merge({
        isVisible: true,
        newPostRecipients: payload.newPostRecipients,
        disablePosts: true,
        disableObservations: false,
        observationVariant: payload.observationVariant,
        selectedRecipients: undefined,
    });
};

const showObservationDraftReducer: PayloadReducer<DraftManagerModalState, Actions.ShowObservationDraftPayload> = (
    state,
    {payload},
) => {
    return DraftManagerModalStateRecord({
        isVisible: true,
        disablePosts: true,
        singleDraftMode: true,
        observationVariant: payload.observationVariant ? payload.observationVariant : state.observationVariant,
        selectedRecipients: payload.selectedRecipients,
        newPostRecipients: undefined,
        feedItem: undefined,
        context: '',
        disableObservations: false,
        initialValues: undefined,
    });
};

const showWithInitialValuesReducer: PayloadReducer<DraftManagerModalState, ShowWithInitialValuesPayload> = (
    _,
    {payload},
) => {
    return DraftManagerModalStateRecord({
        isVisible: true,
        initialValues: payload,
        singleDraftMode: true,
        newPostRecipients: undefined,
        feedItem: undefined,
        context: '',
        disablePosts: false,
        disableObservations: false,
        observationVariant: ObservationVariantValues.REGULAR_OBSERVATION,
        selectedRecipients: undefined,
    });
};

const hideReducer: PayloadReducer<DraftManagerModalState, Actions.HidePayload> = () => {
    return DraftManagerModalStateRecord();
};

const updateItemSuccessReducer: PayloadReducer<DraftManagerModalState, any> = () => {
    return DraftManagerModalStateRecord();
};

const initialState = DraftManagerModalStateRecord();

export default makeReducer<DraftManagerModalState>(
    [
        match(Actions.show, showReducer),
        match(Actions.showObservation, showObservationReducer),
        match(Actions.showObservationDraft, showObservationDraftReducer),
        match(TypedActions.showWithInitialValues, showWithInitialValuesReducer),
        match(Actions.hide, hideReducer),
        match(FeedItemActions.updateItemSuccess, updateItemSuccessReducer),
    ],
    initialState,
);
